import React, {useCallback, useContext, useEffect, useRef, useState, createContext} from "react";
import {useSearchParams} from "react-router-dom";
import {Layout} from "../../pages/siren/Layout";
import {useAuth} from "../../context/user";
import {usePermissions} from "../../context/modulePermissions";
import CodeMirror from "@uiw/react-codemirror";
import {css} from "@codemirror/lang-css";
import {BlueButton} from "../../components/ui/Buttons";
import {ToggleButton} from "./import/inputs";
import {SirenPortalBranding} from "./hooks/useGetSirenPortalBrandings";
import {useUpsertSirenPortalBranding} from "./hooks/useUpsertSirenPortalBranding";
import {useDeleteSirenPortalBranding} from "./hooks/useDeleteSirenPortalBranding";
import {platformAPI, platformFormAPI} from "../../api/axiosConfig";
import {SelectList, SelectListHandle, SelectListItem, SelectListPage} from "../../components/select-list/SelectList";
import {useEvents} from "../../lib/useEvents";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";

type FormState = {
    domains: string[];
    disableDefaultStyle: boolean;
    theme: string;
    customCSS: string;
    pageTitle: string;
    cspImgSrc: string[];
    cspFontSrc: string[];
    cspStyleSrc: string[];
};

const emptyForm = (): FormState => ({domains: [], disableDefaultStyle: false, theme: "", customCSS: "", pageTitle: "", cspImgSrc: [], cspFontSrc: [], cspStyleSrc: []});

const LightbulbToggle: React.FC<{dark: boolean; onToggle: () => void}> = ({dark, onToggle}) => (
    <button
        type="button"
        title="Toggle dark background"
        onClick={onToggle}
        className="absolute top-3 right-3 p-0.5 rounded transition-colors"
    >
        {dark
            ? <LightbulbIcon style={{fontSize: 22}} className="text-white"/>
            : <LightbulbOutlinedIcon style={{fontSize: 22}} className="text-light-grey"/>
        }
    </button>
);

const CREATE_NEW_ID = "__create_new__";

type BrandingListItem = {id: string; label: string; enabled: boolean};

const TogglesEnabledContext = createContext(false);

function EnabledBadge({enabled}: {enabled: boolean}) {
    return (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${enabled ? "bg-desaturated-green text-dark-green" : "bg-desaturated-grey text-grey"}`}>
            {enabled ? "Enabled" : "Disabled"}
        </span>
    );
}

function BrandingListLabel({item, onToggle}: {item: BrandingListItem; onToggle: (id: string, enabled: boolean, e: React.MouseEvent) => void}) {
    const togglesEnabled = useContext(TogglesEnabledContext);
    const {listen} = useEvents();
    const [enabled, setEnabled] = useState(item.enabled);

    useEffect(() => {
        return listen("siren:portalBranding:changed", ({id, enabled: newEnabled}) => {
            if (id === item.id && newEnabled !== undefined) setEnabled(!!newEnabled);
        });
    }, [item.id, listen]);

    return (
        <span className="flex items-center gap-2 w-full">
            <span className="text-sm flex-1 min-w-0 truncate">{item.label}</span>
            <button
                type="button"
                onClick={e => onToggle(item.id, enabled, e)}
                title={togglesEnabled ? undefined : "Unlock to toggle"}
                className={`shrink-0 ${togglesEnabled ? "cursor-pointer" : "cursor-default opacity-60"}`}
            >
                <EnabledBadge enabled={enabled}/>
            </button>
        </span>
    );
}

// --- Inline panel ---

type PortalBrandingPanelProps = {
    recordID: string | null;
    clientID: string;
    onSaved: (record: SirenPortalBranding) => void;
    onDeleted: () => void;
};

const PortalBrandingPanel: React.FC<PortalBrandingPanelProps> = ({recordID, clientID, onSaved, onDeleted}) => {
    const {save, saving, error: saveError} = useUpsertSirenPortalBranding();
    const {deleteBranding, deleting} = useDeleteSirenPortalBranding();
    const {hasFullAccess} = usePermissions();

    const [record, setRecord] = useState<SirenPortalBranding | null>(null);
    const [loading, setLoading] = useState(!!recordID);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [saved, setSaved] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [domainInput, setDomainInput] = useState("");
    const [cspImgInput, setCspImgInput] = useState("");
    const [cspFontInput, setCspFontInput] = useState("");
    const [cspStyleInput, setCspStyleInput] = useState("");

    const [logoUploading, setLogoUploading] = useState(false);
    const [logoProgress, setLogoProgress] = useState(0);
    const [logoProcessing, setLogoProcessing] = useState(false);
    const [logoRemoving, setLogoRemoving] = useState(false);
    const [logoRemoveConfirm, setLogoRemoveConfirm] = useState(false);
    const [logoDarkBg, setLogoDarkBg] = useState(false);
    const [logoPreviewFile, setLogoPreviewFile] = useState<File | null>(null);
    const [logoPreviewURL, setLogoPreviewURL] = useState<string | null>(null);
    const [logoPreviewDark, setLogoPreviewDark] = useState(false);
    const [logoError, setLogoError] = useState<string | null>(null);
    const [logoObjectURL, setLogoObjectURL] = useState<string | null>(null);
    const [newLogoObjectURL, setNewLogoObjectURL] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const logoPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [faviconUploading, setFaviconUploading] = useState(false);
    const [faviconProcessing, setFaviconProcessing] = useState(false);
    const [faviconRemoving, setFaviconRemoving] = useState(false);
    const [faviconRemoveConfirm, setFaviconRemoveConfirm] = useState(false);
    const [faviconObjectURL, setFaviconObjectURL] = useState<string | null>(null);
    const [faviconError, setFaviconError] = useState<string | null>(null);
    const faviconInputRef = useRef<HTMLInputElement>(null);
    const faviconPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!recordID) { setLoading(false); return; }
        setLoading(true);
        platformAPI.get<SirenPortalBranding>(`api/siren/branding/${recordID}`)
            .then(res => {
                setRecord(res.data);
                setForm({
                    domains: res.data.domains ?? [],
                    disableDefaultStyle: res.data.disableDefaultStyle ?? false,
                    theme: res.data.theme ?? "",
                    customCSS: res.data.customCSS ?? "",
                    pageTitle: res.data.pageTitle ?? "",
                    cspImgSrc: res.data.cspImgSrc ?? [],
                    cspFontSrc: res.data.cspFontSrc ?? [],
                    cspStyleSrc: res.data.cspStyleSrc ?? [],
                });
            })
            .finally(() => setLoading(false));
    }, [recordID]);

    useEffect(() => {
        if (!record?.id || record.logoStatus !== "ready") { setLogoObjectURL(null); return; }
        let url: string | null = null;
        platformAPI.get<Blob>(`api/siren/branding/${record.id}/logo`, {responseType: "blob"})
            .then(res => { url = URL.createObjectURL(res.data); setLogoObjectURL(url); })
            .catch(() => setLogoObjectURL(null));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [record?.id, record?.logoStatus]);

    useEffect(() => {
        if (!record?.id || record.faviconStatus !== "ready") { setFaviconObjectURL(null); return; }
        let url: string | null = null;
        platformAPI.get<Blob>(`api/siren/branding/${record.id}/favicon`, {responseType: "blob"})
            .then(res => { url = URL.createObjectURL(res.data); setFaviconObjectURL(url); })
            .catch(() => setFaviconObjectURL(null));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [record?.id, record?.faviconStatus]);

    useEffect(() => () => { if (logoPollRef.current) clearTimeout(logoPollRef.current); }, []);
    useEffect(() => () => { if (faviconPollRef.current) clearTimeout(faviconPollRef.current); }, []);
    useEffect(() => () => { if (logoPreviewURL) URL.revokeObjectURL(logoPreviewURL); }, [logoPreviewURL]);

    const addDomain = useCallback(() => {
        const trimmed = domainInput.trim();
        if (!trimmed || form.domains.includes(trimmed)) return;
        setForm(f => ({...f, domains: [...f.domains, trimmed]}));
        setDomainInput("");
    }, [domainInput, form.domains]);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: record?.id ? undefined : clientID,
                domains: form.domains,
                disableDefaultStyle: form.disableDefaultStyle,
                theme: form.theme,
                customCSS: form.customCSS,
                pageTitle: form.pageTitle,
                cspImgSrc: form.cspImgSrc,
                cspFontSrc: form.cspFontSrc,
                cspStyleSrc: form.cspStyleSrc,
            }, record?.id);
            setRecord(result);
            setNewLogoObjectURL(null);
            setSaved(true);
            onSaved(result);
        } catch {
            // error shown via saveError
        }
    }, [form, record, clientID, save, onSaved]);

    const handleDelete = useCallback(async () => {
        if (!record) return;
        setDeleteConfirm(false);
        try {
            await deleteBranding(record.id);
            onDeleted();
        } catch {
            // silently ignore
        }
    }, [record, deleteBranding, onDeleted]);

    const handleLogoUpload = useCallback(async (file: File) => {
        if (!record?.id) return;
        setLogoError(null);
        setLogoProgress(0);
        setLogoUploading(true);
        setLogoProcessing(false);
        setNewLogoObjectURL(null);
        if (logoPollRef.current) clearTimeout(logoPollRef.current);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await platformFormAPI.post<SirenPortalBranding>(`api/siren/branding/${record.id}/logo`, fd, {
                onUploadProgress: (e) => { if (e.total) setLogoProgress(Math.round((e.loaded / e.total) * 100)); },
            });
            setRecord(res.data);
            setLogoProcessing(true);
            const brandingId = res.data.id;
            const poll = () => {
                logoPollRef.current = setTimeout(async () => {
                    try {
                        const check = await platformAPI.get<SirenPortalBranding>(`api/siren/branding/${brandingId}`);
                        if (check.data.logoStatus === "ready") {
                            setLogoProcessing(false);
                            const img = await platformAPI.get<Blob>(`api/siren/branding/${brandingId}/logo`, {responseType: "blob"});
                            setNewLogoObjectURL(URL.createObjectURL(img.data));
                        } else { poll(); }
                    } catch { setLogoProcessing(false); }
                }, 2000);
            };
            poll();
        } catch (e: any) {
            setLogoError(e?.response?.data?.errors?.file?.[0] ?? e?.response?.data?.error ?? "error uploading logo");
        } finally { setLogoUploading(false); }
    }, [record]);

    const handleLogoRemove = useCallback(async () => {
        if (!record?.id) return;
        setLogoRemoving(true);
        try {
            const res = await platformAPI.delete<SirenPortalBranding>(`api/siren/branding/${record.id}/logo`);
            setRecord(res.data);
            setLogoObjectURL(null);
            setNewLogoObjectURL(null);
            if (logoPollRef.current) clearTimeout(logoPollRef.current);
            setLogoProcessing(false);
        } catch { } finally { setLogoRemoving(false); }
    }, [record]);

    const handleFaviconUpload = useCallback(async (file: File) => {
        if (!record?.id) return;
        setFaviconError(null);
        setFaviconUploading(true);
        setFaviconProcessing(false);
        if (faviconPollRef.current) clearTimeout(faviconPollRef.current);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await platformFormAPI.post<SirenPortalBranding>(`api/siren/branding/${record.id}/favicon`, fd);
            setRecord(res.data);
            setFaviconProcessing(true);
            const brandingId = res.data.id;
            const poll = () => {
                faviconPollRef.current = setTimeout(async () => {
                    try {
                        const check = await platformAPI.get<SirenPortalBranding>(`api/siren/branding/${brandingId}`);
                        if (check.data.faviconStatus === "ready") {
                            setFaviconProcessing(false);
                            const img = await platformAPI.get<Blob>(`api/siren/branding/${brandingId}/favicon`, {responseType: "blob"});
                            setFaviconObjectURL(URL.createObjectURL(img.data));
                        } else { poll(); }
                    } catch { setFaviconProcessing(false); }
                }, 2000);
            };
            poll();
        } catch (e: any) {
            setFaviconError(e?.response?.data?.errors?.file?.[0] ?? e?.response?.data?.error ?? "error uploading favicon");
        } finally { setFaviconUploading(false); }
    }, [record]);

    const handleFaviconRemove = useCallback(async () => {
        if (!record?.id) return;
        setFaviconRemoving(true);
        try {
            const res = await platformAPI.delete<SirenPortalBranding>(`api/siren/branding/${record.id}/favicon`);
            setRecord(res.data);
            setFaviconObjectURL(null);
            if (faviconPollRef.current) clearTimeout(faviconPollRef.current);
            setFaviconProcessing(false);
        } catch { } finally { setFaviconRemoving(false); }
    }, [record]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <div className={`flex flex-col gap-4 p-5 overflow-y-auto${!hasFullAccess("siren_config") ? " pointer-events-none" : ""}`}>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 items-start">

                {/* Left column: Domains + Options + CSP */}
                <div className="flex flex-col gap-4">

                    {/* Domains */}
                    <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                        <label className="font-medium text-sm">Domains</label>
                        {form.domains.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {form.domains.map(d => (
                                    <span key={d} className="inline-flex items-center gap-1 bg-light-light-grey text-sm rounded-full px-3 py-1">
                                        <span className="font-mono">{d}</span>
                                        <button type="button" className="ml-1 text-light-grey hover:text-red leading-none" onClick={() => setForm(f => ({...f, domains: f.domains.filter(x => x !== d)}))}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <input
                                className="flex-1 h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                                placeholder="e.g. alerts.client.com"
                                value={domainInput}
                                onChange={e => setDomainInput(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addDomain(); } }}
                            />
                            <button type="button" className="h-9 px-3 bg-blue text-white rounded-md shadow-md flex items-center gap-1 text-sm hover:bg-opacity-90 disabled:opacity-50" disabled={!domainInput.trim()} onClick={addDomain}>
                                <AddIcon style={{fontSize: 16}}/>Add
                            </button>
                        </div>
                    </div>

                    {/* Options */}
                    <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="font-medium text-sm">Default Style</label>
                            <ToggleButton text="Disable Default Style" value={form.disableDefaultStyle} onChange={v => setForm(f => ({...f, disableDefaultStyle: v}))}/>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="font-medium text-sm">Theme</label>
                            <input className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm" placeholder="leave blank for default" value={form.theme} onChange={e => setForm(f => ({...f, theme: e.target.value}))}/>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="font-medium text-sm">Page Title</label>
                            <input className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm" placeholder="e.g. Acme Corp Alerts" value={form.pageTitle} onChange={e => setForm(f => ({...f, pageTitle: e.target.value}))}/>
                        </div>
                    </div>

                    {/* CSP */}
                    <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3">
                        <p className="text-sm font-semibold text-light-grey uppercase tracking-wide">Content Security Policy</p>
                        <p className="text-xs text-light-grey -mt-2">Extra allowed sources beyond <code className="font-mono">&apos;self&apos;</code>. Use for external fonts, images, or stylesheets loaded by your custom CSS.</p>
                        {(["cspImgSrc", "cspFontSrc", "cspStyleSrc"] as const).map(field => {
                            const labels = {cspImgSrc: "Image Sources", cspFontSrc: "Font Sources", cspStyleSrc: "Style Sources"};
                            const placeholders = {cspImgSrc: "e.g. cdn.example.com", cspFontSrc: "e.g. fonts.gstatic.com", cspStyleSrc: "e.g. fonts.googleapis.com"};
                            const inputs = {cspImgSrc: cspImgInput, cspFontSrc: cspFontInput, cspStyleSrc: cspStyleInput};
                            const setters = {cspImgSrc: setCspImgInput, cspFontSrc: setCspFontInput, cspStyleSrc: setCspStyleInput};
                            const input = inputs[field];
                            const setInput = setters[field];
                            const addEntry = () => {
                                const trimmed = input.trim();
                                if (!trimmed || form[field].includes(trimmed)) return;
                                setForm(f => ({...f, [field]: [...f[field], trimmed]}));
                                setInput("");
                            };
                            return (
                                <div key={field} className="flex flex-col gap-2">
                                    <label className="font-medium text-sm">{labels[field]}</label>
                                    {form[field].length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {form[field].map(src => (
                                                <span key={src} className="inline-flex items-center gap-1 bg-light-light-grey text-sm rounded-full px-3 py-1">
                                                    <span className="font-mono">{src}</span>
                                                    <button type="button" className="ml-1 text-light-grey hover:text-red leading-none" onClick={() => setForm(f => ({...f, [field]: f[field].filter(x => x !== src)}))}>×</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <input className="flex-1 h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm" placeholder={placeholders[field]} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEntry(); } }}/>
                                        <button type="button" className="h-9 px-3 bg-blue text-white rounded-md shadow-md flex items-center gap-1 text-sm hover:bg-opacity-90 disabled:opacity-50" disabled={!input.trim()} onClick={addEntry}>
                                            <AddIcon style={{fontSize: 16}}/>Add
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right column: Logo + Favicon */}
                <div className="flex flex-col gap-4">

                    {/* Logo */}
                    <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                        <label className="font-medium text-sm">Logo</label>
                        {!record?.id ? (
                            <p className="text-sm text-light-grey">Save first to enable logo upload.</p>
                        ) : (
                            <>
                                {logoObjectURL && (
                                    <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-24 transition-colors ${logoDarkBg ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                                        <LightbulbToggle dark={logoDarkBg} onToggle={() => setLogoDarkBg(v => !v)}/>
                                        <img src={logoObjectURL} alt="Brand logo" className="max-h-full max-w-full object-contain p-2"/>
                                    </div>
                                )}
                                {logoUploading && <div className="w-full bg-light-light-grey rounded-full h-2 overflow-hidden"><div className="bg-blue h-2 rounded-full transition-all duration-200" style={{width: `${logoProgress}%`}}/></div>}
                                {logoProcessing && <p className="text-sm text-light-grey">Processing…</p>}
                                {!logoUploading && newLogoObjectURL && (
                                    <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-24 transition-colors ${logoDarkBg ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                                        <LightbulbToggle dark={logoDarkBg} onToggle={() => setLogoDarkBg(v => !v)}/>
                                        <img src={newLogoObjectURL} alt="New brand logo" className="max-h-full max-w-full object-contain p-2"/>
                                    </div>
                                )}
                                {!logoObjectURL && !logoUploading && !logoProcessing && !newLogoObjectURL && <p className="text-sm text-light-grey">No logo uploaded</p>}
                                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                                    onChange={e => { const f = e.target.files?.[0]; if (f) { setLogoPreviewFile(f); setLogoPreviewURL(URL.createObjectURL(f)); setLogoPreviewDark(false); } e.target.value = ""; }}
                                />
                                {hasFullAccess("siren_config") && (
                                    <div className="flex gap-2 flex-wrap">
                                        <button type="button" className="px-4 h-9 rounded-md bg-blue text-white text-sm font-medium hover:bg-opacity-90 disabled:opacity-50" disabled={logoUploading || logoRemoving} onClick={() => logoInputRef.current?.click()}>
                                            {record.logoStatus ? "Replace Logo" : "Upload Logo"}
                                        </button>
                                        {record.logoStatus && (
                                            <button type="button" className="flex items-center gap-1.5 px-3 h-9 rounded-md border border-red text-red text-sm font-medium hover:bg-red hover:text-white transition-colors disabled:opacity-50" disabled={logoUploading || logoRemoving} onClick={() => setLogoRemoveConfirm(true)}>
                                                <DeleteIcon style={{fontSize: 16}}/>Remove
                                            </button>
                                        )}
                                    </div>
                                )}
                                {logoError && <p className="text-sm text-red">{logoError}</p>}
                            </>
                        )}
                    </div>

                    {/* Favicon */}
                    <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                        <label className="font-medium text-sm">Favicon</label>
                        {!record?.id ? (
                            <p className="text-sm text-light-grey">Save first to enable favicon upload.</p>
                        ) : (
                            <>
                                {faviconObjectURL && <div className="flex items-center justify-center rounded-md border border-light-light-grey h-16 bg-light-light-grey bg-opacity-30"><img src={faviconObjectURL} alt="Favicon" className="max-h-full max-w-full object-contain p-2"/></div>}
                                {faviconUploading && <p className="text-sm text-light-grey">Uploading…</p>}
                                {faviconProcessing && <p className="text-sm text-light-grey">Processing…</p>}
                                {!faviconObjectURL && !faviconUploading && !faviconProcessing && <p className="text-sm text-light-grey">No favicon uploaded</p>}
                                <input ref={faviconInputRef} type="file" accept="image/png,image/svg+xml" className="hidden"
                                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFaviconUpload(f); e.target.value = ""; }}
                                />
                                {hasFullAccess("siren_config") && (
                                    <div className="flex gap-2 flex-wrap">
                                        <button type="button" className="px-4 h-9 rounded-md bg-blue text-white text-sm font-medium hover:bg-opacity-90 disabled:opacity-50" disabled={faviconUploading || faviconRemoving} onClick={() => faviconInputRef.current?.click()}>
                                            {record.faviconStatus ? "Replace Favicon" : "Upload Favicon"}
                                        </button>
                                        {record.faviconStatus && (
                                            <button type="button" className="flex items-center gap-1.5 px-3 h-9 rounded-md border border-red text-red text-sm font-medium hover:bg-red hover:text-white transition-colors disabled:opacity-50" disabled={faviconUploading || faviconRemoving} onClick={() => setFaviconRemoveConfirm(true)}>
                                                <DeleteIcon style={{fontSize: 16}}/>Remove
                                            </button>
                                        )}
                                    </div>
                                )}
                                {faviconError && <p className="text-sm text-red">{faviconError}</p>}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Custom CSS — full width */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                <label className="font-medium text-sm">Custom CSS</label>
                <div className="rounded-md overflow-hidden border border-light-light-grey max-h-64 overflow-y-auto">
                    <CodeMirror value={form.customCSS} extensions={[css()]} onChange={v => setForm(f => ({...f, customCSS: v}))} placeholder="/* custom styles */" minHeight="120px" basicSetup={{lineNumbers: true, foldGutter: false}}/>
                </div>
            </div>

            {saveError && <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>}
            {saved && <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Saved</div>}

            {hasFullAccess("siren_config") && (
                <div className="flex justify-end gap-2 items-center flex-wrap">
                    {record && (
                        <button type="button" className="h-10 px-3 rounded-md bg-red text-white hover:bg-opacity-80 transition-colors flex items-center gap-1.5 text-sm font-medium" onClick={() => setDeleteConfirm(true)}>
                            <DeleteIcon style={{fontSize: 16}}/>Delete
                        </button>
                    )}
                    <div className="w-32">
                        <BlueButton text="Save" onClick={handleSave} loading={saving} disabled={form.domains.length === 0 || logoUploading || logoRemoving || saving}/>
                    </div>
                </div>
            )}

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete Brand</h3>
                        <p className="text-sm">Delete branding for <span className="font-mono font-semibold">{form.domains.join(", ") || "this record"}</span>?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={deleting} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {logoRemoveConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Remove Logo</h3>
                        <p className="text-sm">Remove the logo for this brand?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setLogoRemoveConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={logoRemoving} onClick={() => { setLogoRemoveConfirm(false); handleLogoRemove(); }}>Remove</button>
                        </div>
                    </div>
                </div>
            )}

            {faviconRemoveConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Remove Favicon</h3>
                        <p className="text-sm">Remove the favicon for this brand?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setFaviconRemoveConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={faviconRemoving} onClick={() => { setFaviconRemoveConfirm(false); handleFaviconRemove(); }}>Remove</button>
                        </div>
                    </div>
                </div>
            )}

            {logoPreviewURL && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 w-full max-w-lg">
                        <h3 className="font-bold">Preview Logo</h3>
                        <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-48 transition-colors ${logoPreviewDark ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                            <LightbulbToggle dark={logoPreviewDark} onToggle={() => setLogoPreviewDark(v => !v)}/>
                            <img src={logoPreviewURL} alt="Logo preview" className="max-h-full max-w-full object-contain p-2"/>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => { URL.revokeObjectURL(logoPreviewURL); setLogoPreviewURL(null); setLogoPreviewFile(null); }}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-blue text-white" onClick={() => { URL.revokeObjectURL(logoPreviewURL); setLogoPreviewURL(null); if (logoPreviewFile) handleLogoUpload(logoPreviewFile); setLogoPreviewFile(null); }}>Upload</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Main page ---

type ClientItem = {id: string; name: string};

export const SirenPortalBrandingPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {isSuperAdmin, clientID: authClientID} = useAuth();
    const {hasFullAccess} = usePermissions();
    const [selectedClientID, setSelectedClientID] = useState<string | null>(() => {
        if (!isSuperAdmin) return authClientID || null;
        return searchParams.get("clientID");
    });
    const [selectedClientName, setSelectedClientName] = useState<string | null>(null);
    const [selectedBrandingID, setSelectedBrandingID] = useState<string | null>(null);
    const [togglesEnabled, setTogglesEnabled] = useState(false);
    const togglesEnabledRef = useRef(false);
    const brandingListRef = useRef<SelectListHandle>(null);
    const {emit} = useEvents();

    useEffect(() => {
        if (!selectedClientID || selectedClientName) return;
        platformAPI.get<{id: string; name: string}>(`api/clients/${selectedClientID}`)
            .then(res => setSelectedClientName(res.data.name))
            .catch(() => {
                setSelectedClientID(null);
                setSearchParams(p => { p.delete("clientID"); return p; }, {replace: true});
            });
    }, []);

    const handleSelectClient = useCallback((id: string, name: string) => {
        setSelectedClientID(id);
        setSelectedClientName(name);
        setSelectedBrandingID(null);
        setSearchParams(p => { p.set("clientID", id); return p; }, {replace: true});
    }, [setSearchParams]);

    const handleClearClient = useCallback(() => {
        setSelectedClientID(null);
        setSelectedClientName(null);
        setSelectedBrandingID(null);
        setSearchParams(p => { p.delete("clientID"); return p; }, {replace: true});
    }, [setSearchParams]);

    const toggleEnabled = useCallback(async (id: string, currentEnabled: boolean, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!togglesEnabledRef.current) return;
        const newEnabled = !currentEnabled;
        emit("siren:portalBranding:changed", {id, enabled: newEnabled});
        try {
            await platformAPI.patch(`api/siren/branding/${id}/enabled`, {enabled: newEnabled});
        } catch {
            emit("siren:portalBranding:changed", {id, enabled: currentEnabled});
        }
    }, [emit]);

    const getClients = useCallback(async (search: string, page: number): Promise<SelectListPage<ClientItem>> => {
        const res = await platformAPI.get<{items: ClientItem[]; totalPages: number}>("api/clients", {
            params: {search, page: page + 1, limit: 12, module: "siren"},
        });
        return {
            items: res.data.items.map(c => ({item: c, label: c.name})),
            totalPages: Math.max(1, res.data.totalPages),
        };
    }, []);

    const getBrandings = useCallback(async (search: string): Promise<SelectListItem<BrandingListItem>[]> => {
        if (!selectedClientID) return [];
        try {
            const res = await platformAPI.get<{results: SirenPortalBranding[]}>("api/siren/branding", {
                params: {search, limit: 50, clientID: selectedClientID},
            });
            return (res.data.results ?? []).map(b => {
                const label = (b.domains ?? []).join(", ") || b.id;
                const item: BrandingListItem = {id: b.id, label, enabled: b.enabled};
                return {item, label: <BrandingListLabel item={item} onToggle={toggleEnabled}/>};
            });
        } catch {
            return [];
        }
    }, [selectedClientID, toggleEnabled]);

    const brandingListHeader = (
        <div className="flex flex-col border-b border-light-light-grey">
            {isSuperAdmin && (
                <button
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium text-blue hover:bg-light-light-grey transition-colors border-b border-light-light-grey"
                    onClick={handleClearClient}
                >
                    <ArrowBackIcon style={{fontSize: 16}}/>
                    <span>Clients</span>
                    <span className="text-light-grey font-normal truncate">/ {selectedClientName}</span>
                </button>
            )}
            {hasFullAccess("siren_config") && (
                <div className="flex border-b border-light-light-grey">
                    <button
                        type="button"
                        className="flex-1 px-4 py-3 text-left text-sm flex items-center gap-1.5 hover:bg-light-light-grey"
                        onClick={() => setSelectedBrandingID(CREATE_NEW_ID)}
                    >
                        <AddCircleOutlineIcon style={{fontSize: 16}}/>Add Branding
                    </button>
                    <button
                        type="button"
                        title={togglesEnabled ? "Lock enabled toggles" : "Unlock enabled toggles"}
                        className={`px-3 flex items-center hover:bg-light-light-grey ${togglesEnabled ? "text-blue" : "text-light-grey"}`}
                        onClick={() => {
                            const next = !togglesEnabled;
                            togglesEnabledRef.current = next;
                            setTogglesEnabled(next);
                        }}
                    >
                        {togglesEnabled ? <LockOpenOutlinedIcon style={{fontSize: 18}}/> : <LockOutlinedIcon style={{fontSize: 18}}/>}
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <TogglesEnabledContext.Provider value={togglesEnabled}>
            <Layout menuCurrent="config">
                <div className="flex gap-4 h-full">
                    <div className="w-[460px] shrink-0">
                        {isSuperAdmin && !selectedClientID ? (
                            <SelectList<ClientItem>
                                getItems={getClients}
                                selectedID={null}
                                pageSize={12}
                                onSelect={c => handleSelectClient(c.id, c.name)}
                            />
                        ) : (
                            <SelectList<BrandingListItem>
                                ref={brandingListRef}
                                getItems={getBrandings}
                                selectedID={selectedBrandingID}
                                onSelect={item => setSelectedBrandingID(item.id)}
                                header={brandingListHeader}
                            />
                        )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                        {isSuperAdmin && !selectedClientID && (
                            <div className="p-5 text-sm text-light-grey">Select a client to view portal brandings.</div>
                        )}
                        {selectedClientID && !selectedBrandingID && (
                            <div className="p-5 text-sm text-light-grey">Select a branding to edit, or create a new one.</div>
                        )}
                        {selectedClientID && selectedBrandingID && (
                            <PortalBrandingPanel
                                key={selectedBrandingID}
                                recordID={selectedBrandingID === CREATE_NEW_ID ? null : selectedBrandingID}
                                clientID={selectedClientID}
                                onSaved={result => {
                                    brandingListRef.current?.refresh();
                                    setSelectedBrandingID(result.id);
                                }}
                                onDeleted={() => {
                                    brandingListRef.current?.refresh();
                                    setSelectedBrandingID(null);
                                }}
                            />
                        )}
                    </div>
                </div>
            </Layout>
        </TogglesEnabledContext.Provider>
    );
};

export default SirenPortalBrandingPage;
